import React, { useState } from "react";
import ToolLayout from "@/components/ToolLayout";
import PostResultCTA from "@/components/PostResultCTA";
import useSEO from "@/hooks/useSEO";

function calcSla({ artifactsPerWeek, reviewers, targetApprovalRate, avgReviewHours }) {
  const capacityHoursPerWeek = reviewers * 40 * 0.3;
  const requiredHoursPerWeek = artifactsPerWeek * avgReviewHours;
  const utilization = capacityHoursPerWeek > 0 ? requiredHoursPerWeek / capacityHoursPerWeek : 99;
  const queueMultiplier = Math.max(1, 1 + utilization * 0.5);
  const estimatedSlaHours = Math.round(avgReviewHours * queueMultiplier);

  const approvalCapacity = Math.round(capacityHoursPerWeek / avgReviewHours);
  const targetCapacity = Math.ceil(artifactsPerWeek / (targetApprovalRate / 100));
  const recommendedReviewers =
    utilization > 0.85
      ? Math.ceil((targetCapacity * avgReviewHours) / (40 * 0.3))
      : reviewers;

  return {
    capacityHoursPerWeek: Math.round(capacityHoursPerWeek),
    requiredHoursPerWeek: Math.round(requiredHoursPerWeek),
    utilization: Math.min(utilization, 9.99),
    estimatedSlaHours,
    approvalCapacity,
    recommendedReviewers,
    status:
      utilization < 0.7 ? "healthy" : utilization < 0.9 ? "warning" : "overloaded",
  };
}

const STATUS_STYLES = {
  healthy: {
    label: "Healthy capacity",
    color: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
  },
  warning: {
    label: "Approaching capacity",
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
  },
  overloaded: {
    label: "Over capacity",
    color: "text-rose-700",
    bg: "bg-rose-50 border-rose-200",
  },
};

function NumInput({ label, hint, value, onChange, min = 1, max, step = 1 }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {hint && <p className="text-xs text-muted-foreground mb-1.5">{hint}</p>}
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
      />
    </div>
  );
}

export default function ReviewSlaCalculator() {
  useSEO({
    title: "Knowledge Review SLA Calculator — Free Tool — ProofMark",
    description:
      "Calculate realistic SLAs for your AI knowledge review process. Input team size, volume, and target approval rate to get capacity utilisation and SLA estimates.",
    path: "/tools/review-sla-calculator",
  });

  const [inputs, setInputs] = useState({
    artifactsPerWeek: 20,
    reviewers: 3,
    targetApprovalRate: 80,
    avgReviewHours: 2,
  });

  const set = (key) => (val) => setInputs((i) => ({ ...i, [key]: val }));
  const result = calcSla(inputs);
  const statusStyle = STATUS_STYLES[result.status];

  return (
    <ToolLayout>
      <div className="mb-8">
        <span className="tracked-caps">Free Tool</span>
        <h1 className="font-serif-display text-4xl mt-2 mb-3">Review SLA Calculator</h1>
        <p className="text-muted-foreground text-lg max-w-2xl">
          Input your team size, weekly artifact volume, and target approval rate. Get a realistic
          SLA estimate, capacity utilisation score, and recommended team size.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="space-y-5">
          <NumInput
            label="New artifacts per week"
            hint="How many candidate artifacts enter the review queue each week."
            value={inputs.artifactsPerWeek}
            onChange={set("artifactsPerWeek")}
          />
          <NumInput
            label="Number of reviewers"
            hint="Full-time reviewers with bandwidth for knowledge review."
            value={inputs.reviewers}
            onChange={set("reviewers")}
          />
          <NumInput
            label="Target approval rate (%)"
            hint="What percentage of submitted artifacts should reach approved status."
            value={inputs.targetApprovalRate}
            onChange={set("targetApprovalRate")}
            min={1}
            max={100}
          />
          <NumInput
            label="Average review time (hours)"
            hint="How long a thorough review of one artifact takes, end to end."
            value={inputs.avgReviewHours}
            onChange={set("avgReviewHours")}
            step={0.5}
          />
          <p className="text-xs text-muted-foreground">
            Assumes reviewers spend 30% of their time on reviews (12h/week).
          </p>
        </div>

        {/* Results — live as inputs change */}
        <div className="space-y-4">
          <div className={`border rounded-lg p-5 ${statusStyle.bg}`}>
            <span className="tracked-caps mb-2 block">Capacity status</span>
            <p className={`text-xl font-medium ${statusStyle.color}`}>{statusStyle.label}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {Math.round(result.utilization * 100)}% utilised ·{" "}
              {result.capacityHoursPerWeek}h capacity vs {result.requiredHoursPerWeek}h required/week
            </p>
          </div>

          <div className="border border-border rounded-lg p-5 bg-card">
            <span className="tracked-caps mb-3 block">Estimates</span>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Estimated SLA</p>
                <p className="font-mono text-2xl font-medium">{result.estimatedSlaHours}h</p>
                <p className="text-xs text-muted-foreground">avg time to approved</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Weekly throughput</p>
                <p className="font-mono text-2xl font-medium">{result.approvalCapacity}</p>
                <p className="text-xs text-muted-foreground">artifacts at capacity</p>
              </div>
            </div>
          </div>

          {result.recommendedReviewers !== inputs.reviewers && (
            <div className="border border-amber-200 rounded-lg p-5 bg-amber-50">
              <p className="text-sm font-medium text-amber-700 mb-1">Recommendation</p>
              <p className="text-sm text-muted-foreground">
                To maintain &lt;90% utilisation at your target approval rate, consider{" "}
                <strong>{result.recommendedReviewers} reviewers</strong> (currently{" "}
                {inputs.reviewers}).
              </p>
            </div>
          )}

          <PostResultCTA
            openLoop={`At ${Math.round(result.utilization * 100)}% utilisation, ${result.status === "over" ? "you're over capacity — SLA breaches are likely without intervention" : result.status === "warning" ? "you're approaching the edge of SLA breach territory" : "your capacity looks healthy, but volume spikes will expose the margin"}. ProofMark routes artifacts by contributor class automatically, reducing manual triage by ~60%.`}
            source="review-sla-calculator"
            nextPath="/tools/revocation-risk"
            nextLabel="Next: score your revocation risk →"
            testimonial={null}
          />
        </div>
      </div>

      {/* Educational content */}
      <div className="mt-20 space-y-10 border-t border-border/80 pt-12">
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            How to set realistic SLAs for knowledge review
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            Most teams underestimate the time a thorough knowledge review requires. Reading the
            source evidence, checking for policy flags, editing the draft, and writing a decision
            comment typically takes 1.5–3 hours for a Class 2 artifact. Setting an SLA without
            accounting for reviewer capacity leads to either missed SLAs or shortcuts that
            undermine review quality. The right SLA is the minimum time to maintain quality at
            current volume — not an aspirational number.
          </p>
        </section>
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            Common causes of knowledge review bottlenecks
          </h2>
          <div className="space-y-3 max-w-3xl">
            {[
              "Reviewers spending more than 30% of time on review (bandwidth competition with primary role)",
              "Unclear artifact requirements causing multiple revision cycles before approval",
              "No parallel review — artifacts queuing behind each other rather than progressing simultaneously",
              "Missing source evidence requiring reviewers to track down records manually",
              "Policy ambiguity causing reviewers to escalate decisions unnecessarily",
            ].map((item, i) => (
              <div key={i} className="flex gap-3 text-sm text-muted-foreground">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            Benchmarks: review SLAs at high-performing teams
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            High-performing knowledge governance teams typically target 24–48 hours for Class 1
            artifacts, 48–72 hours for Class 2, and 5–10 business days for Class 3. These are
            first-response SLAs — the time from submission to a decision (approve, dispute, or
            send back). Teams that instrument their review pipeline and track SLA by domain can
            identify bottlenecks before they create deployment delays.
          </p>
        </section>
      </div>
    </ToolLayout>
  );
}
